import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Dialog } from '../../common/Catalyst/dialog';
import { Button } from '../../common/Catalyst/button';
import { HoaReturn, ViolationCreate, ViolationReturn } from '../../clients/rust-api-client';
import { violationService } from '../../clients/ApiClients';
import { apiErrorMessage } from '../../utils/apiErrors';
import { ErrorAlert } from '../../common/Custom/ErrorAlert';
import { DatePicker, PropertyPicker } from './PropertyPicker';
import { ScopeChips, useScopeState } from '../../shell/ScopeChips';
import { CreateScope } from '../../shell/createScope';
import { SeverityGlyph, StatusIcon } from './StatusIcon';
import {
  SEVERITIES,
  STATUSES,
  VIOLATION_TYPES,
  ViolationStatus,
  formatDay,
  noonUtcMillis,
  statusLabel,
} from './violationMeta';
import { CalendarIcon, TagIcon } from '@heroicons/react/16/solid';

/**
 * Linear's lightweight create (#603 Part 2 §6): a large title, a free-form
 * body, and a row of property chips using the same pickers as the detail
 * page — not a grid of labelled inputs.
 *
 * Two things stay deliberately explicit rather than chip-like:
 *  - the fine, because a fine is a ledger charge on the home's account;
 *  - notifying the homeowners, because it sends email.
 * Both are plain, labelled controls in the footer.
 *
 * Where it is for — association and home — leads the chip row (#632):
 * pre-filled from the page, or picked here when it was opened from the
 * sidebar, the palette or an unscoped list.
 */
interface Props {
  open: boolean;
  hoas: HoaReturn[];
  hoasLoading?: boolean;
  scope?: CreateScope;
  /** The page fixed the scope it passed; those chips are not changeable. */
  lockScope?: boolean;
  onClose: () => void;
  onCreated: (violation: ViolationReturn) => void;
}

const todayUtc = () => new Date().toISOString().slice(0, 10);
const FINE_PATTERN = /^\d{0,5}(\.\d{0,2})?$/;

export const CreateViolationModal: React.FC<Props> = ({
  open,
  hoas,
  hoasLoading,
  scope = {},
  lockScope,
  onClose,
  onCreated,
}) => {
  const { hoaId, setHoaId, homeId, setHomeId } = useScopeState(open, scope, hoas);
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState<string | null>(null);
  const [severity, setSeverity] = useState<string | null>(null);
  const [status, setStatus] = useState<ViolationStatus>('Pending');
  const [observed, setObserved] = useState(todayUtc());
  const [due, setDue] = useState('');
  const [fine, setFine] = useState('');
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDescription('');
    setMessage('');
    setType(null);
    setSeverity(null);
    setStatus('Pending');
    setObserved(todayUtc());
    setDue('');
    setFine('');
    setNotify(true);
    setError(null);
  }, [open]);

  const fineValue = fine.trim();
  const fineValid = fineValue === '' || (FINE_PATTERN.test(fineValue) && Number(fineValue) <= 10000);
  const missing = [
    !homeId && 'a home',
    !description.trim() && 'a title',
    !message.trim() && 'a message',
    !type && 'a type',
    !severity && 'a severity',
  ].filter(Boolean) as string[];
  const canSubmit = missing.length === 0 && fineValid && !saving;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !homeId || !type || !severity) return;
    const observedMs = noonUtcMillis(observed);
    if (observedMs === null) {
      setError('The observed date is not a valid date.');
      return;
    }
    const payload: ViolationCreate = {
      home_id: homeId,
      description: description.trim(),
      message: message.trim(),
      violation_type: type,
      severity,
      status,
      date_of_violation: observedMs,
      due_date: due ? noonUtcMillis(due) : null,
      fine_amount: fineValue && Number(fineValue) > 0 ? fineValue : null,
      send_notification: notify,
    };
    setSaving(true);
    setError(null);
    try {
      const response = await violationService.createViolation({ violationCreate: payload });
      onCreated(response.data);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'The violation was not created. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const placeholderChip = (label: string, icon?: React.ReactNode) => (
    <span className="inline-flex items-center gap-1.5 text-fg-muted">
      {icon}
      {label}
    </span>
  );

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} size="2xl">
      <form onSubmit={submit} aria-label="New violation">
        <p className="text-[12px] font-medium text-fg-muted">
          New violation
        </p>

        <input
          autoFocus
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={300}
          placeholder="What was observed?"
          aria-label="Title"
          className="mt-2 w-full border-0 bg-transparent p-0 text-xl font-semibold text-fg placeholder:text-fg-faint focus:outline-none focus:ring-0"
        />
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          placeholder="Message to the homeowner — the rule, what was seen, what to do."
          aria-label="Message to the homeowner"
          className="mt-3 w-full resize-none border-0 bg-transparent p-0 text-[15px]/6 text-fg-secondary placeholder:text-fg-faint focus:outline-none focus:ring-0"
        />

        <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-line/60 pt-3">
          <ScopeChips
            hoas={hoas}
            hoasLoading={hoasLoading}
            hoaId={hoaId}
            onHoaChange={setHoaId}
            lockHoa={lockScope && !!scope.hoaId}
            memberId={scope.memberId}
            home={{
              homeId,
              onChange: setHomeId,
              locked: lockScope && !!scope.homeId,
            }}
          />
          <PropertyPicker
            options={STATUSES.map((s) => ({ value: s.value, label: s.label, icon: <StatusIcon status={s.value} /> }))}
            value={status}
            onChange={(v) => setStatus(v as ViolationStatus)}
            ariaLabel={`Status: ${statusLabel(status)}`}
            trigger={<><StatusIcon status={status} />{statusLabel(status)}</>}
          />
          <PropertyPicker
            options={SEVERITIES.map((s) => ({ value: s, label: s, icon: <SeverityGlyph severity={s} /> }))}
            value={severity}
            onChange={setSeverity}
            ariaLabel={severity ? `Severity: ${severity}` : 'Choose severity'}
            trigger={severity ? <><SeverityGlyph severity={severity} />{severity}</> : placeholderChip('Severity', <SeverityGlyph severity="" />)}
          />
          <PropertyPicker
            options={VIOLATION_TYPES.map((t) => ({ value: t, label: t }))}
            value={type}
            onChange={setType}
            ariaLabel={type ? `Type: ${type}` : 'Choose type'}
            trigger={type ? <><TagIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />{type}</> : placeholderChip('Type', <TagIcon className="h-3.5 w-3.5" aria-hidden />)}
          />
          <DatePicker
            value={observed}
            onChange={setObserved}
            ariaLabel={`Observed: ${formatDay(`${observed}T12:00:00Z`)}`}
            trigger={<><CalendarIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />Observed {formatDay(`${observed}T12:00:00Z`)}</>}
          />
          <DatePicker
            value={due}
            onChange={setDue}
            ariaLabel={due ? `Due: ${formatDay(`${due}T12:00:00Z`)}` : 'Set due date'}
            trigger={due ? <><CalendarIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />Due {formatDay(`${due}T12:00:00Z`)}</> : placeholderChip('Due date', <CalendarIcon className="h-3.5 w-3.5" aria-hidden />)}
          />
        </div>

        <ErrorAlert className="mt-3">{error}</ErrorAlert>

        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-line/60 pt-4">
          <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
            Fine
            <span
              className={clsx(
                'flex items-center rounded-md border px-2 py-1',
                fineValid ? 'border-line' : 'border-danger'
              )}
            >
              <span className="text-fg-muted">$</span>
              <input
                inputMode="decimal"
                value={fine}
                onChange={(e) => setFine(e.target.value.replace(/[^\d.]/g, ''))}
                placeholder="0.00"
                aria-invalid={!fineValid}
                aria-describedby="fine-help"
                className="w-20 border-0 bg-transparent p-0 pl-1 text-[13px] text-fg focus:outline-none focus:ring-0"
              />
            </span>
          </label>
          <span id="fine-help" className="sr-only">
            Optional. Up to $10,000.00. Charged to the home&apos;s account on create.
          </span>
          <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="h-4 w-4 rounded border-line-strong bg-transparent text-accent focus:ring-accent"
            />
            Notify the homeowners
          </label>
          <div className="ml-auto flex items-center gap-2">
            <Button plain type="button" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              color="sky"
              type="submit"
              disabled={!canSubmit}
              title={missing.length ? `Add ${missing.join(', ')}` : undefined}
            >
              {saving ? 'Creating…' : 'Create violation'}
            </Button>
          </div>
        </div>
        {missing.length > 0 && (description || message || type || severity) && (
          <p className="mt-2 text-right text-[12px] text-fg-muted">
            Still needs {missing.join(', ')}.
          </p>
        )}
      </form>
    </Dialog>
  );
};
